<template>
  <div class="media-viewer" :class="{ 'fullscreen': isFullscreen }">
    <div class="media-container">
      <!-- Debug info -->
      <div class="debug-info">
        <p>Source: {{ src }}</p>
        <p>Type: {{ isVideo ? 'video' : 'image' }}</p>
      </div>
      
      <!-- Video Player -->
      <video 
        v-if="isVideo"
        ref="videoElement"
        class="media-element"
        :src="src" 
        :poster="poster"
        :controls="showControls" 
        :autoplay="autoplay"
        :loop="loop"
        @error="handleMediaError"
        @loadedmetadata="handleMediaLoaded"
        @click="toggleFullscreen"
      ></video>

      <!-- Image Viewer -->
      <img 
        v-else
        class="media-element"
        :src="src" 
        :alt="alt" 
        @error="handleMediaError" 
        @load="handleMediaLoaded"
        @click="toggleFullscreen"
      />

      <!-- Loading indicator -->
      <div v-if="isLoading" class="loading-indicator">
        <div class="spinner"></div>
      </div>

      <!-- Error message -->
      <div v-if="hasError" class="error-message">
        <span>{{ errorMessage || 'Failed to load media' }}</span>
      </div>

      <!-- Fullscreen button -->
      <div v-if="!isFullscreen && !isLoading && !hasError" class="media-controls">
        <button class="fullscreen-btn" @click.stop="toggleFullscreen" title="Toggle fullscreen">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
          </svg>
        </button>
      </div>
      
      <!-- Close button for fullscreen mode -->
      <button v-if="isFullscreen" class="close-btn" @click.stop="closeFullscreen" title="Exit fullscreen">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';

export default {
  name: 'MediaViewer',
  props: {
    src: {
      type: String,
      required: true
    },
    type: {
      type: String,
      default: 'auto' // 'auto', 'image', or 'video'
    },
    poster: {
      type: String,
      default: ''
    },
    alt: {
      type: String,
      default: 'Media content'
    },
    showControls: {
      type: Boolean,
      default: true
    },
    autoplay: {
      type: Boolean,
      default: false
    },
    loop: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const isLoading = ref(true);
    const hasError = ref(false);
    const errorMessage = ref('');
    const isFullscreen = ref(false);
    const videoElement = ref(null);
    
    // Determine media type
    const isVideo = computed(() => {
      if (props.type === 'video') return true;
      if (props.type === 'image') return false;
      
      // Auto-detect from URL if type is 'auto'
      if (props.src) {
        const extension = props.src.split('.').pop()?.toLowerCase();
        return ['mp4', 'webm', 'ogg', 'mov', 'm4v'].includes(extension);
      }
      
      return false;
    });
    
    // Handle media loading events
    const handleMediaLoaded = () => {
      isLoading.value = false;
      hasError.value = false;
    };
    
    // Handle media error events
    const handleMediaError = (event) => {
      isLoading.value = false;
      hasError.value = true;
      errorMessage.value = `Failed to load media: ${event.target.error?.message || 'Unknown error'}`;
    };
    
    // Toggle fullscreen mode
    const toggleFullscreen = () => {
      isFullscreen.value = !isFullscreen.value;
      
      // Add/remove body class to prevent scrolling
      if (isFullscreen.value) {
        document.body.classList.add('media-viewer-open');
      } else {
        document.body.classList.remove('media-viewer-open');
      }
    };
    
    // Close fullscreen mode
    const closeFullscreen = () => {
      isFullscreen.value = false;
      document.body.classList.remove('media-viewer-open');
    };
    
    // Handle escape key for exiting fullscreen
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && isFullscreen.value) {
        closeFullscreen();
      }
    };
    
    // Add event listeners on mount
    onMounted(() => {
      window.addEventListener('keydown', handleKeyDown);
    });
    
    // Remove event listeners before unmount
    onBeforeUnmount(() => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.classList.remove('media-viewer-open');
    });
    
    return {
      isLoading,
      hasError,
      errorMessage,
      isFullscreen,
      isVideo,
      videoElement,
      handleMediaLoaded,
      handleMediaError,
      toggleFullscreen,
      closeFullscreen
    };
  }
};
</script>

<style scoped>
.media-viewer {
  position: relative;
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  background-color: #1a1a1a;
}

.debug-info {
  padding: 8px;
  background-color: rgba(0, 0, 0, 0.5);
  color: #fff;
  font-size: 12px;
  border-radius: 4px;
  margin-bottom: 8px;
}

.debug-info p {
  margin: 2px 0;
}

.media-viewer.fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 9999;
  border-radius: 0;
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
}

.media-container {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 150px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #111827;
  padding: 10px;
}

.media-element {
  width: 100%;
  min-height: 150px;
  max-height: 300px;
  object-fit: contain;
  display: block;
  transition: transform 0.3s ease;
  cursor: pointer;
  border: 1px solid #4b5563;
}

.fullscreen .media-element {
  max-height: 90vh;
  max-width: 90vw;
  box-shadow: 0 0 30px rgba(0, 0, 0, 0.5);
}

.loading-indicator {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  justify-content: center;
  align-items: center;
}

.error-message {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: rgba(239, 68, 68, 0.8);
  color: white;
  padding: 12px 16px;
  border-radius: 6px;
  text-align: center;
  max-width: 80%;
}

.spinner {
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top: 3px solid #fff;
  width: 30px;
  height: 30px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.media-controls {
  position: absolute;
  top: 10px;
  right: 10px;
  display: flex;
  gap: 8px;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.media-viewer:hover .media-controls {
  opacity: 1;
}

.fullscreen-btn, .close-btn {
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  border-radius: 4px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s;
}

.fullscreen-btn:hover, .close-btn:hover {
  background-color: rgba(0, 0, 0, 0.7);
}

.close-btn {
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 10;
}

/* Prevent scrolling when modal is open */
:global(body.media-viewer-open) {
  overflow: hidden;
}
</style> 